import AssignmentIcon from "@mui/icons-material/Assignment";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import React from "react";
import { NavigateFunction } from "react-router";
import {
  AlertsAndNotification,
  AlertsAndNotificationSource,
  AppNotificationContextValues,
} from "../../../../../../../../context/AppNotificationContext/AppNotificationContext";

const SURPLUS_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  surplusReturn:
    "alertsAndNotifications:alertsAndNotifications.surplusType.surplusReturn",
  closeOutReturn:
    "alertsAndNotifications:alertsAndNotifications.surplusType.closeOutReturn",
  programReturn:
    "alertsAndNotifications:alertsAndNotifications.surplusType.programReturn",
};

function getSurplusReturnLabel(surplusType: string, t: any): string {
  const key =
    SURPLUS_TYPE_TRANSLATION_KEYS[surplusType] ??
    "alertsAndNotifications:alertsAndNotifications.surplusType.surplusReturn";
  return t(key);
}

interface AlertCategoryConfigProps {
  getHeading: (
    alert: AlertsAndNotification,
    t: any,
    isMultipleAccountViewActive: boolean
  ) => string;
  getMessage: (alert: AlertsAndNotification, t: any) => string;
  getButtonText: (t: any) => string;
  onButtonClick: (
    alert: AlertsAndNotification,
    navigate: NavigateFunction,
    markAlertAsRead?: AppNotificationContextValues["markAlertsAsRead"]
  ) => void;
  getIcon?: () => React.ReactElement;
}

export const alertCategoryConfig: Record<string, AlertCategoryConfigProps> = {
  dailyReturnsNeedAction: {
    getHeading: (alert, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnsNeedAction.withAccountId",
            {
              returnId: alert.payload.returnId,
              accountId: alert.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnsNeedAction",
            { returnId: alert.payload.returnId }
          ),
    getMessage: (alert, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.dailyReturnsNeedAction",
        { returnId: alert.payload.returnId }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.dailyReturnsNeedAction"
      ),
    onButtonClick: (alert, navigate) => {
      navigate(`/returns/${alert.payload.returnId}?dealerId=${alert.dealerId}`);
    },
  },
  dailyReturnDealerPreparation: {
    getHeading: (alert, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnDealerPreparation.withAccountId",
            {
              returnId: alert.payload.returnId,
              accountId: alert.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.dailyReturnDealerPreparation",
            { returnId: alert.payload.returnId }
          ),
    getMessage: (alert, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.dailyReturnDealerPreparation",
        { returnId: alert.payload.returnId }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.dailyReturnDealerPreparation"
      ),
    onButtonClick: (alert, navigate) => {
      navigate(`/returns/${alert.payload.returnId}?dealerId=${alert.dealerId}`);
    },
  },
  claimsNeedAction: {
    getHeading: (alert, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.claimsNeedAction.withAccountId",
            {
              claimId: alert.payload.claimId,
              accountId: alert.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.claimsNeedAction",
            { claimId: alert.payload.claimId }
          ),
    getMessage: (alert, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.claimsNeedAction",
        { claimId: alert.payload.claimId }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.claimsNeedAction"
      ),
    onButtonClick: (alert, navigate) => {
      navigate(`/claims/${alert.payload.claimId}`, {
        state: { dealerAccountId: alert.dealerId },
      });
    },
  },
  d2dOrderRequestNeedAction: {
    getHeading: (alert, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.d2dSellerOrderRequests.withAccountId",
            {
              orderId: alert.payload.orderId ?? alert.payload.orderNumber,
              accountId: alert.dealerId,
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.d2dSellerOrderRequests",
            { orderId: alert.payload.orderId ?? alert.payload.orderNumber }
          ),
    getMessage: (alert, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.d2dSellerOrderRequests",
        {
          orderId: alert.payload.orderId ?? alert.payload.orderNumber,
          partNumber: alert.payload.partNumber,
          orderedQuantity: alert.payload.orderedQuantity,
        }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.d2dSellerOrderRequests"
      ),
    onButtonClick: (alert, navigate, markAlertAsRead) => {
      navigate(`/dealer/${alert.dealerId}/d2d-requests`);
      markAlertAsRead?.([
        {
          id: alert.id,
          source: AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE,
        },
      ]);
    },
  },
  surplusReturnsNeedAction: {
    getHeading: (alert, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnsNeedAction.withAccountId",
            {
              returnId: alert.payload.returnId,
              accountId: alert.dealerId,
              returnType: getSurplusReturnLabel(alert.payload.surplusType, t),
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnsNeedAction",
            {
              returnId: alert.payload.returnId,
              returnType: getSurplusReturnLabel(alert.payload.surplusType, t),
            }
          ),
    getMessage: (alert, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.surplusReturnsNeedAction",
        { returnId: alert.payload.returnId }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.surplusReturnsNeedAction"
      ),
    onButtonClick: (alert, navigate) => {
      navigate(
        `/returns/surplus/${alert.payload.returnId}?dealerId=${alert.dealerId}`
      );
    },
  },
  surplusReturnsWaitingForNfe: {
    getHeading: (alert, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnsWaitingForNfe.withAccountId",
            {
              returnId: alert.payload.returnId,
              accountId: alert.dealerId,
              returnType: getSurplusReturnLabel(alert.payload.surplusType, t),
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnsWaitingForNfe",
            {
              returnId: alert.payload.returnId,
              returnType: getSurplusReturnLabel(alert.payload.surplusType, t),
            }
          ),
    getMessage: (alert, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.surplusReturnsWaitingForNfe",
        { returnId: alert.payload.returnId }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.surplusReturnsWaitingForNfe"
      ),
    onButtonClick: (alert, navigate) => {
      navigate(
        `/returns/surplus/${alert.payload.returnId}?dealerId=${alert.dealerId}`
      );
    },
    getIcon: () =>
      React.createElement(TimerOutlinedIcon, {
        sx: { color: "var(--fuel-color-alert-warning-icon)" },
      }),
  },
  surplusReturnsDealerPreparation: {
    getHeading: (alert, t, isMultipleAccountViewActive) =>
      isMultipleAccountViewActive
        ? t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnsDealerPreparation.withAccountId",
            {
              returnId: alert.payload.returnId,
              accountId: alert.dealerId,
              returnType: getSurplusReturnLabel(alert.payload.surplusType, t),
            }
          )
        : t(
            "alertsAndNotifications:alertsAndNotifications.cell.title.surplusReturnsDealerPreparation",
            {
              returnId: alert.payload.returnId,
              returnType: getSurplusReturnLabel(alert.payload.surplusType, t),
            }
          ),
    getMessage: (alert, t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.description.surplusReturnsDealerPreparation",
        { returnId: alert.payload.returnId }
      ),
    getButtonText: (t) =>
      t(
        "alertsAndNotifications:alertsAndNotifications.cell.actionButton.surplusReturnsDealerPreparation"
      ),
    onButtonClick: (alert, navigate) => {
      navigate(
        `/returns/surplus/${alert.payload.returnId}?dealerId=${alert.dealerId}`
      );
    },
    getIcon: () =>
      React.createElement(AssignmentIcon, {
        sx: { color: "var(--fuel-color-alert-warning-icon)" },
      }),
  },
};
